import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { ApiError } from "../../api/client";
import { HierarchyApi, type Branch, type Constituency, type District, type Ward } from "../../admin/hierarchy/api";
import { PollingStationsApi, type PollingStation } from "../../admin/pollingStations/api";
import { RacesApi, type Race } from "../../admin/races/api";
import { ErrorPanel } from "../../shell/ErrorPanel";
import { PVTApi, type PVTCandidate } from "./api";

/**
 * PVT (Parallel Vote Tabulation) capture — an intelligence officer transcribes
 * the official Gen-12 / tally sheet at a polling station. One submission per
 * (polling-station, race, period); resubmitting upserts the same row so a
 * second observer can correct an earlier mistake.
 *
 * The form is intentionally narrow: pick the polling station + race, type in
 * each candidate's name + vote count, plus invalid votes. We compute valid /
 * total / turnout live so the officer can sanity-check before saving.
 */
export function PVTCapture() {
  const nav = useNavigate();
  const qc = useQueryClient();

  const [did, setDid] = useState("");
  const [cid, setCid] = useState("");
  const [wid, setWid] = useState("");
  const [bid, setBid] = useState("");
  const [psid, setPsid] = useState("");
  const [raceId, setRaceId] = useState("");
  const [period, setPeriod] = useState(String(new Date().getFullYear()));
  const [candidates, setCandidates] = useState<PVTCandidate[]>([
    { name: "", party: "", votes: 0 },
    { name: "", party: "", votes: 0 },
  ]);
  const [invalidVotes, setInvalidVotes] = useState("0");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<unknown>(null);

  const districts = useQuery({ queryKey: ["districts"], queryFn: HierarchyApi.listDistricts });
  const constituencies = useQuery({
    queryKey: ["constituencies", did],
    queryFn: () => HierarchyApi.listConstituenciesInDistrict(did),
    enabled: !!did,
  });
  const wards = useQuery({
    queryKey: ["wards", cid],
    queryFn: () => HierarchyApi.listWards(cid),
    enabled: !!cid,
  });
  const branches = useQuery({
    queryKey: ["branches", wid],
    queryFn: () => HierarchyApi.listBranches(wid),
    enabled: !!wid,
  });
  const stations = useQuery({
    queryKey: ["polling-stations", bid],
    queryFn: () => PollingStationsApi.listForBranch(bid),
    enabled: !!bid,
  });
  const races = useQuery({ queryKey: ["races"], queryFn: RacesApi.list });

  // Reset child selections when a parent changes.
  useEffect(() => { setCid(""); setWid(""); setBid(""); setPsid(""); }, [did]);
  useEffect(() => { setWid(""); setBid(""); setPsid(""); }, [cid]);
  useEffect(() => { setBid(""); setPsid(""); }, [wid]);
  useEffect(() => { setPsid(""); }, [bid]);

  const station = useMemo(
    () => (stations.data?.items ?? []).find((s) => s.id === psid),
    [stations.data, psid],
  );
  const registered = station?.registeredVoters ?? 0;

  const validVotes = candidates.reduce((s, c) => s + (Number(c.votes) || 0), 0);
  const invalid = Math.max(0, Number(invalidVotes) || 0);
  const totalVotes = validVotes + invalid;
  const turnoutPct = registered > 0 ? (totalVotes / registered) * 100 : 0;
  const overTurnout = registered > 0 && totalVotes > registered;

  // Pre-fill existing submission for this station+race+period (upsert mode).
  const existing = useQuery({
    queryKey: ["pvt-result-existing", psid, raceId, period],
    queryFn: () => PVTApi.list({ pollingStationId: psid, raceId, period }),
    enabled: !!psid && !!raceId && !!period,
  });
  useEffect(() => {
    const row = existing.data?.items?.[0];
    if (!row) return;
    setCandidates(row.candidates.length ? row.candidates : candidates);
    setInvalidVotes(String(row.invalidVotes));
    setNotes(row.notes || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.data?.items?.[0]?.id]);

  const create = useMutation({
    mutationFn: () => PVTApi.create({
      pollingStationId: psid,
      raceId,
      period,
      candidates: candidates
        .filter((c) => c.name.trim() !== "")
        .map((c) => ({ name: c.name.trim(), party: (c.party || "").trim(), votes: Math.max(0, Number(c.votes) || 0) })),
      invalidVotes: invalid,
      notes,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pvt-results"] });
      nav("/app/voters");
    },
    onError: (e) => setError(e),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!psid) { setError(new ApiError("Pick a polling station to record against.", 400)); return; }
    if (!raceId) { setError(new ApiError("Pick a race (President / MP / Mayor / Councillor).", 400)); return; }
    const filledCandidates = candidates.filter((c) => c.name.trim() !== "");
    if (filledCandidates.length === 0) {
      setError(new ApiError("Add at least one candidate with a name.", 400));
      return;
    }
    if (overTurnout) {
      setError(new ApiError(`Total votes (${totalVotes}) exceed registered (${registered}) at this station. Re-check the tally sheet.`, 400));
      return;
    }
    create.mutate();
  }

  function setCandidate(i: number, patch: Partial<PVTCandidate>) {
    setCandidates((arr) => arr.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  }

  function addCandidate() {
    setCandidates((arr) => [...arr, { name: "", party: "", votes: 0 }]);
  }

  function removeCandidate(i: number) {
    setCandidates((arr) => (arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr));
  }

  return (
    <div>
      <Link to=".." className="muted" style={{ fontSize: 13 }}>← Back to voters</Link>
      <h1 style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 600 }}>Capture PVT result</h1>
      <p className="muted" style={{ margin: "0 0 16px", fontSize: 14 }}>
        Transcribe the official Gen-12 / tally sheet from a polling station. One row per
        (polling-station, race, period). Re-submitting the same combination overwrites
        the prior capture — second observers can correct an earlier mistake.
      </p>

      <ErrorPanel err={error} title="Couldn't capture PVT result" />

      <form onSubmit={submit} className="card" style={{ padding: 16 }}>
        <div className="row-3" style={{ gap: 8 }}>
          <Field label="District">
            <select value={did} onChange={(e) => setDid(e.target.value)}>
              <option value="">— Select —</option>
              {districts.data?.items.map((d: District) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
          <Field label="Constituency">
            <select value={cid} onChange={(e) => setCid(e.target.value)} disabled={!did}>
              <option value="">— Select —</option>
              {constituencies.data?.items.map((c: Constituency) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Ward">
            <select value={wid} onChange={(e) => setWid(e.target.value)} disabled={!cid}>
              <option value="">— Select —</option>
              {wards.data?.items.map((w: Ward) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </Field>
        </div>

        <div className="row-3" style={{ gap: 8, marginTop: 8 }}>
          <Field label="Branch">
            <select value={bid} onChange={(e) => setBid(e.target.value)} disabled={!wid}>
              <option value="">— Select —</option>
              {branches.data?.items.map((b: Branch) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
          <Field label="Polling station">
            <select value={psid} onChange={(e) => setPsid(e.target.value)} disabled={!bid}>
              <option value="">— Select —</option>
              {stations.data?.items.map((ps: PollingStation) => <option key={ps.id} value={ps.id}>{ps.name}</option>)}
            </select>
          </Field>
          <Field label="Period">
            <input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="e.g. 2026" />
          </Field>
        </div>

        <div className="row-3" style={{ gap: 8, marginTop: 8 }}>
          <Field label="Race">
            <select value={raceId} onChange={(e) => setRaceId(e.target.value)}>
              <option value="">— Select —</option>
              {races.data?.items.map((r: Race) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </Field>
          <Field label="Invalid / rejected votes">
            <input
              type="number" min="0"
              value={invalidVotes}
              onChange={(e) => setInvalidVotes(e.target.value)}
            />
          </Field>
          <span />
        </div>

        {/* Live tally summary */}
        {(validVotes > 0 || invalid > 0) && (
          <div style={{
            marginTop: 16, padding: 12, borderRadius: 6,
            background: overTurnout ? "var(--danger-soft, #fee2e2)" : "var(--bg-muted, #f7f7f9)",
            color: overTurnout ? "var(--danger)" : undefined,
          }}>
            <div style={{ fontSize: 13 }}>
              <strong>Valid:</strong> {validVotes.toLocaleString()} ·{" "}
              <strong>Invalid:</strong> {invalid.toLocaleString()} ·{" "}
              <strong>Total:</strong> {totalVotes.toLocaleString()}
              {registered > 0 && (
                <> · <strong>Turnout:</strong> {turnoutPct.toFixed(1)}% of {registered.toLocaleString()} registered</>
              )}
            </div>
            {overTurnout && (
              <div style={{ fontSize: 12, marginTop: 4 }}>
                Total votes exceed the registered roll for this station — likely a tally-sheet
                transcription error. Re-check before saving.
              </div>
            )}
            {registered === 0 && psid && (
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                No registered-voter count on file for this polling station — turnout % can't be
                computed. Set it in Setup → Polling stations.
              </div>
            )}
          </div>
        )}

        {/* Per-candidate rows */}
        <div style={{ marginTop: 16, padding: 12, background: "var(--bg-muted, #f7f7f9)", borderRadius: 6 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Candidates</div>
            <button type="button" onClick={addCandidate} className="ghost" style={{ padding: "4px 10px", fontSize: 12 }}>
              + Add candidate
            </button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--muted)" }}>
                <th style={{ padding: "4px 8px" }}>Name</th>
                <th style={{ padding: "4px 8px" }}>Party</th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>Votes</th>
                <th style={{ padding: "4px 8px", width: 50 }} />
              </tr>
            </thead>
            <tbody>
              {candidates.map((c, i) => (
                <tr key={i} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ padding: "4px 8px" }}>
                    <input
                      value={c.name}
                      onChange={(e) => setCandidate(i, { name: e.target.value })}
                      placeholder="Candidate name"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td style={{ padding: "4px 8px" }}>
                    <input
                      value={c.party}
                      onChange={(e) => setCandidate(i, { party: e.target.value })}
                      placeholder="Party (optional)"
                      style={{ width: "100%" }}
                    />
                  </td>
                  <td style={{ padding: "4px 8px", textAlign: "right" }}>
                    <input
                      type="number" min="0"
                      value={c.votes}
                      onChange={(e) => setCandidate(i, { votes: Number(e.target.value) || 0 })}
                      style={{ maxWidth: 100, textAlign: "right" }}
                    />
                  </td>
                  <td style={{ padding: "4px 8px", textAlign: "right" }}>
                    {candidates.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCandidate(i)}
                        className="ghost"
                        style={{ padding: "2px 8px", fontSize: 11, color: "var(--danger)" }}
                        aria-label={`Remove candidate row ${i + 1}`}
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 12 }}>
          <Field label="Notes (optional)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything unusual at this station — disputed ballots, late opening, observer interference, etc."
              rows={2}
              style={{ width: "100%" }}
            />
          </Field>
        </div>

        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <button type="submit" disabled={create.isPending}>
            {create.isPending ? "Saving…" : (existing.data?.items?.[0] ? "Update PVT" : "Save PVT")}
          </button>
          <Link to=".." className="ghost" style={{ padding: "6px 12px" }}>Cancel</Link>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 11, color: "var(--muted)", marginBottom: 2 }}>{label}</label>
      {children}
    </div>
  );
}
